/**
 * Correct the delivery address during the confirmation call (Rx-02). Starts from the address the
 * patient gave; saving replaces the order's address only (not the patient's address book), records
 * the change in the audit trail, and puts the order back to "address not confirmed". The claimant
 * can do this until the price is published (PHA-21, D-23). The state is picked from the API's list:
 * it decides CGST + SGST vs IGST, and the API refuses a code it doesn't know (ACCT-07).
 */

import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { describeError } from "../../api/errors";
import { reviseDeliveryAddress } from "../../api/orders";
import type { PharmacistOrder } from "../../api/orders";
import { StateSelect } from "../../components/ui/StateSelect";
import { isBlank } from "../../lib/form";
import { isValidIndianPhone, localDigits } from "../../lib/phone";
import { isValidPinCode, pinDigits } from "../../lib/pincode";

interface Draft {
  name: string;
  phone: string; // 10 local digits
  line1: string;
  line2: string;
  landmark: string;
  city: string;
  state_code: string;
  pin_code: string;
  reason: string;
}

/** What's wrong with each field, by field name (empty when the draft can be saved). */
function addressProblems(d: Draft): Partial<Record<keyof Draft, string>> {
  const problems: Partial<Record<keyof Draft, string>> = {};
  if (isBlank(d.name)) problems.name = "Enter the recipient's name.";
  if (!isValidIndianPhone(d.phone)) problems.phone = "Enter a 10-digit mobile number starting with 6–9.";
  if (isBlank(d.line1)) problems.line1 = "Enter the house / street.";
  if (isBlank(d.city)) problems.city = "Enter the city.";
  if (isBlank(d.state_code)) problems.state_code = "Choose the state.";
  if (!isValidPinCode(d.pin_code)) problems.pin_code = "Enter a 6-digit PIN code.";
  return problems;
}

export function AddressCorrection({
  order,
  onSaved,
  onCancel,
}: {
  order: PharmacistOrder;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const snap = order.delivery_address_snapshot as Record<string, string | null | undefined>;
  const [d, setD] = useState<Draft>(() => ({
    name: snap.name ?? "",
    phone: localDigits(snap.phone ?? ""),
    line1: snap.line1 ?? "",
    line2: snap.line2 ?? "",
    landmark: snap.landmark ?? "",
    city: snap.city ?? "",
    state_code: snap.state_code ?? "",
    pin_code: snap.pin_code ?? "",
    reason: "",
  }));
  const [touched, setTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (patch: Partial<Draft>) => setD((cur) => ({ ...cur, ...patch }));
  const problems = addressProblems(d);
  const valid = Object.keys(problems).length === 0;
  const optional = (v: string) => (isBlank(v) ? null : v.trim());

  const save = useMutation({
    mutationFn: () =>
      reviseDeliveryAddress(order.id, {
        name: d.name.trim(),
        phone: `+91${d.phone}`,
        line1: d.line1.trim(),
        line2: optional(d.line2),
        landmark: optional(d.landmark),
        city: d.city.trim(),
        state_code: d.state_code,
        pin_code: d.pin_code,
        reason: optional(d.reason),
      }),
    onSuccess: onSaved,
    onError: (e) => setError(describeError(e)),
  });

  const show = (field: keyof Draft) => (touched ? problems[field] : undefined);

  return (
    <form
      className="mt-3 space-y-3 rounded-lg border border-line bg-gray-50 p-4"
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        setError(null);
        if (valid) save.mutate();
      }}
    >
      <p className="text-sm font-medium text-ink">Correct the delivery address</p>
      <p className="text-xs text-ink-muted">
        This changes the address for this order only; the patient's saved addresses stay as they are. Saving clears
        the address confirmation: read the new address back to the patient and confirm it again.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input label="Recipient name" value={d.name} onChange={(v) => set({ name: v })} problem={show("name")} />
        <Input
          label="Mobile (+91)"
          value={d.phone}
          onChange={(v) => set({ phone: localDigits(v) })}
          problem={show("phone")}
          inputMode="numeric"
        />
        <div className="sm:col-span-2">
          <Input label="House / street" value={d.line1} onChange={(v) => set({ line1: v })} problem={show("line1")} />
        </div>
        <div className="sm:col-span-2">
          <Input label="Area (optional)" value={d.line2} onChange={(v) => set({ line2: v })} />
        </div>
        <Input label="Landmark (optional)" value={d.landmark} onChange={(v) => set({ landmark: v })} />
        <Input label="City" value={d.city} onChange={(v) => set({ city: v })} problem={show("city")} />
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          State
          <StateSelect
            value={d.state_code}
            onChange={(code) => set({ state_code: code })}
            invalid={show("state_code") !== undefined}
            className={`rounded-md pl-2.5 text-sm ${show("state_code") ? "border-red-400" : ""}`}
          />
          {show("state_code") && <span className="text-red-600">{show("state_code")}</span>}
        </label>
        <Input
          label="PIN code"
          value={d.pin_code}
          onChange={(v) => set({ pin_code: pinDigits(v) })}
          problem={show("pin_code")}
          inputMode="numeric"
        />
        <div className="sm:col-span-2">
          <Input
            label="What changed? (optional, kept in the audit trail)"
            value={d.reason}
            onChange={(v) => set({ reason: v })}
          />
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={save.isPending || (touched && !valid)}
          className="rounded-lg bg-brand px-4 py-1.5 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Save address"}
        </button>
        <button type="button" onClick={onCancel} disabled={save.isPending} className="rounded-lg border px-4 py-1.5 text-sm">
          Cancel
        </button>
      </div>
    </form>
  );
}

function Input({
  label,
  value,
  onChange,
  problem,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  problem?: string;
  inputMode?: "numeric";
}) {
  return (
    <label className="flex flex-col gap-1 text-xs text-ink-muted">
      {label}
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode={inputMode}
        aria-invalid={problem !== undefined}
        className={`rounded-md border bg-white px-2.5 py-1.5 text-sm text-ink ${problem ? "border-red-400" : ""}`}
      />
      {problem && <span className="text-red-600">{problem}</span>}
    </label>
  );
}
